import { useMemo } from 'react';

import { useContentfulContentTypeEntry } from '@order/Contentful';

import {
  type ProductDetailsContent,
  type ProductDetailsMappedContent,
  type ProductDetailsScreenContent,
} from '../../ProductDetails.types.ts';

// ─────────────────────────────────────────────────────────────────────────────

export const useProductDetailsContent = (params: Params) => {
  const { baseProductSlug } = params;

  // ─── Remote Data ─────────────────────────────────────────────────────

  const searchParams = useMemo(
    () => ({ 'fields.baseProductSlug': baseProductSlug }),
    [baseProductSlug],
  );

  const { data: screenData, fetching: isFetchingScreenData } =
    useContentfulContentTypeEntry<ProductDetailsScreenContent>({
      contentType: PRODUCT_DETAILS_SCREEN_CONTENT_TYPE,
    });

  const { data: pdpData, fetching: isFetchingPdpData } =
    useContentfulContentTypeEntry<ProductDetailsContent>({
      contentType: PRODUCT_DETAILS_CONTENT_TYPE,
      searchParams,
    });

  // ─── Derived Data ────────────────────────────────────────────────────

  const ingredientHighlights = useMemo(() => {
    const entries = pdpData?.fields?.superIngredients ?? [];

    return entries.map((ingredient) => ingredient.fields);
  }, [pdpData?.fields?.superIngredients]);

  const mappedContent = useMemo<ProductDetailsMappedContent>(
    () => ({
      screenTitle: screenData?.fields?.screenTitle,
      ingredientHighlightsTitle: screenData?.fields?.ingredientHighlightsTitle,
      nutritionInfoTitle: screenData?.fields?.nutritionInfoTitle,

      subheading: pdpData?.fields?.subheading,
      description: pdpData?.fields?.description,
      ingredientHighlights,
      carousel: pdpData?.fields?.carousel ?? [],
      mobileHeaderBackgroundColor: pdpData?.fields?.mobileHeaderBackgroundColor,
      mobileHeaderBackgroundImage: pdpData?.fields?.mobileHeaderBackgroundImage,
      mobileHeaderForegroundColor: pdpData?.fields?.mobileHeaderForegroundColor,
      nutritionistPhoto: pdpData?.fields?.nutritionistPhoto,
      nutritionistTitle: pdpData?.fields?.nutritionistTitle,
      nutritionistLogo: pdpData?.fields?.nutritionistLogo,
      nutritionistLogoWidth: pdpData?.fields?.nutritionistLogoWidth,
      nutritionistLogoHeight: pdpData?.fields?.nutritionistLogoHeight,
    }),
    [
      screenData?.fields?.screenTitle,
      screenData?.fields?.ingredientHighlightsTitle,
      screenData?.fields?.nutritionInfoTitle,
      pdpData?.fields?.subheading,
      pdpData?.fields?.description,
      pdpData?.fields?.carousel,
      pdpData?.fields?.mobileHeaderBackgroundColor,
      pdpData?.fields?.mobileHeaderBackgroundImage,
      pdpData?.fields?.mobileHeaderForegroundColor,
      pdpData?.fields?.nutritionistPhoto,
      pdpData?.fields?.nutritionistTitle,
      pdpData?.fields?.nutritionistLogo,
      pdpData?.fields?.nutritionistLogoWidth,
      pdpData?.fields?.nutritionistLogoHeight,
      ingredientHighlights,
    ],
  );

  // ─────────────────────────────────────────────────────────────────────

  return {
    fetching: isFetchingScreenData || isFetchingPdpData,
    mappedContent,
  };
};

// ─── Constants ───────────────────────────────────────────────────────────────

const PRODUCT_DETAILS_SCREEN_CONTENT_TYPE = 'productDetailsScreen';
const PRODUCT_DETAILS_CONTENT_TYPE = 'productDetails';

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  baseProductSlug: string;
};
